import React, {ReactElement, useEffect, useMemo, useRef, useState} from "react"
import {Button, Collapse, CollapseProps} from "antd"
import {ClearOutlined, PlusOutlined} from "@ant-design/icons"
import {observer} from "mobx-react-lite"
import {AxiosResponse} from "axios"
import {t} from "@ui/common/intl"
import {useStore} from "@ui/stores"
import {pageSize, search} from "@ui/api/poi"
import SwipePanel, {SwipePanelHeight} from "@ui/components/services/common/SwipePanel"
import poiService from "@ui/services/PoiService"
import {PoiPage, TPoiPageProps} from "../"
import Filter from "./Filter"
import List from "./List"
import Category from "./Category"

export enum ActiveKey {
  LIST = 1,
  CATEGORY = 2
}

const Main = ({isShow, close, goto, notifyError}: TPoiPageProps): ReactElement => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const {poi} = useStore()
  const collapseRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (poi.state.activeKey === ActiveKey.LIST && poi.state.list.reload) {
      poi.changeState({list: {reload: false}})
      load()
    }
  }, [])

  useEffect(() => {
    if (isShow) {
      setTimeout(() => {
        const h = (collapseRef.current?.scrollHeight || 0) - 188
        if (h > 0) {
          collapseRef.current?.style.setProperty("--height", `${h}px`)
        }
      }, 700)
    }
  }, [isShow])

  const load = async (page: number = 0): Promise<boolean | void> => {
    setLoading(true)
    setError(false)
    await search({page, filter: poi.filter.params})
      .then(data => {
        if (page === 0) poi.clearListState()
        poi.changeState({list: {more: data.length === pageSize}})
        poi.appendSearch(data)
      })
      .catch((error: AxiosResponse) => {
        setError(true)
        notifyError("poi.list.search.error", error)
        poi.changeState({list: {more: false}})
      })
      .finally(() => setLoading(false))
	}

  const handleNext = () => load(Math.floor((poi.points).length / pageSize))

  const handleAdd = () => {
    goto(PoiPage.New)
  }

  const handleSearch = () => {
    poi.changeState({activeKey: ActiveKey.LIST})
    load()
  }

  const handleClose = () => {
    poi.changeState({activeKey: ActiveKey.CATEGORY})
    poi.filter.clear()
    poiService.clearFilter()
    close()
  }

  const handleClear = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    e.stopPropagation()
    poi.filter.change({spot_type: undefined})
    handleSearch()
  }

  const items: CollapseProps["items"] = useMemo(() => [
    {
      key: 1,
      showArrow: false,
      collapsible: "disabled",
      label: <Filter onSearch={handleSearch} />,
      children: <List
        goto={goto}
        error={error}
        loading={loading}
        onNext={handleNext}
        onSearch={handleSearch}
      />,
    },
    {
      key: 2,
      label: <div className="category" onClick={() => poi.changeState({activeKey: ActiveKey.CATEGORY})}>
        <span className="title">{t("poi.main.category.title")}</span>
        {
          poi.filter.params.spot_type
            ? <span className="selected">
                {t(`poi.category.${poi.filter.params.spot_type}`)}
                <ClearOutlined className="clear-icon" onClick={handleClear} />
              </span>
            : <span>{t("poi.category.not.selected")}</span>
        }
      </div>,
      collapsible: "disabled",
      showArrow: false,
      children: <Category onSearch={handleSearch}/>,
    },
  ], [error, loading, poi.filter.params.spot_type])

  return isShow
    ? (<SwipePanel
        onClose={handleClose}
        onSwipe={close}
        className="main"
        height={SwipePanelHeight.FULL}
        header={
          <>
            <span className="title">{t("poi.main.title")}</span>
            <div className="add-button">
              <Button className="button-regular" shape="round" icon={<PlusOutlined/>} onClick={handleAdd}>
              {t("button.add")}
              </Button>
            </div>
          </>
        }
      >
        <Collapse ref={collapseRef} accordion ghost activeKey={poi.state.activeKey} items={items} />
      </SwipePanel>)
    : (<></>)
}

export default observer(Main)
